/**
 * ContentFlow - Stories 24.6-24.9
 * Six-bucket content flow visualization and interaction
 */

import React, { useState } from 'react';
import {
  Building2, Package, Users, Target, Cpu, TrendingUp,
  Plus, ChevronRight, CheckCircle, Circle, Clock, AlertCircle,
  Sparkles, ArrowUpRight, FileText
} from 'lucide-react';

export type ContentBucket = 
  | 'company'
  | 'product'
  | 'cases'
  | 'competitive'
  | 'technical'
  | 'market';

export interface ContentItem {
  id: string;
  title: string;
  type: string;
  status: 'complete' | 'in-progress' | 'pending' | 'needs-attention';
  lastUpdated?: Date;
  aiScore?: number;
}

export interface BucketData {
  id: ContentBucket;
  label: string;
  icon: React.ElementType;
  color: string;
  items: ContentItem[];
  requiredItems?: number;
  description?: string;
}

interface ContentFlowProps {
  buckets: BucketData[];
  onBucketClick?: (bucketId: ContentBucket) => void;
  onItemClick?: (bucketId: ContentBucket, itemId: string) => void;
  onAddItem?: (bucketId: ContentBucket) => void;
  layout?: 'grid' | 'flow' | 'compact';
  showProgress?: boolean;
  className?: string;
}

export const defaultBuckets: Omit<BucketData, 'items'>[] = [
  { id: 'company', label: 'Company', icon: Building2, color: 'from-blue-500 to-blue-600', description: 'Company overview, mission, values' },
  { id: 'product', label: 'Products', icon: Package, color: 'from-purple-500 to-purple-600', description: 'Product features, benefits, specs' },
  { id: 'cases', label: 'Case Studies', icon: Users, color: 'from-green-500 to-green-600', description: 'Customer success stories' },
  { id: 'competitive', label: 'Competitive', icon: Target, color: 'from-orange-500 to-orange-600', description: 'Competitive positioning, battlecards' },
  { id: 'technical', label: 'Technical', icon: Cpu, color: 'from-cyan-500 to-cyan-600', description: 'Technical documentation, specs' },
  { id: 'market', label: 'Market Intel', icon: TrendingUp, color: 'from-pink-500 to-pink-600', description: 'Market trends, analyst reports' }
];

export const ContentFlow: React.FC<ContentFlowProps> = ({
  buckets,
  onBucketClick,
  onItemClick,
  onAddItem,
  layout = 'grid',
  showProgress = true,
  className = ''
}) => {
  const [expandedBucket, setExpandedBucket] = useState<ContentBucket | null>(null);

  const getStatusIcon = (status: ContentItem['status']) => {
    switch (status) {
      case 'complete': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'in-progress': return <Clock className="w-4 h-4 text-blue-500" />;
      case 'pending': return <Circle className="w-4 h-4 text-gray-400" />;
      case 'needs-attention': return <AlertCircle className="w-4 h-4 text-amber-500" />;
    }
  };

  const getBucketProgress = (bucket: BucketData) => {
    const completed = bucket.items.filter(i => i.status === 'complete').length;
    const total = bucket.requiredItems || bucket.items.length || 1;
    return Math.round((completed / total) * 100);
  };

  const getBucketStatus = (bucket: BucketData) => {
    const progress = getBucketProgress(bucket);
    if (progress === 100) return 'complete';
    if (progress > 0) return 'in-progress';
    return 'empty';
  };

  // Grid layout
  if (layout === 'grid') {
    return (
      <div className={`grid grid-cols-2 md:grid-cols-3 gap-4 ${className}`}>
        {buckets.map((bucket) => {
          const progress = getBucketProgress(bucket);
          const status = getBucketStatus(bucket);
          const isExpanded = expandedBucket === bucket.id;
          const Icon = bucket.icon;

          return (
            <div
              key={bucket.id}
              className={`relative bg-white rounded-xl border-2 transition-all cursor-pointer ${
                status === 'complete' ? 'border-green-300' :
                status === 'in-progress' ? 'border-blue-300' : 'border-gray-200'
              } hover:shadow-lg`}
              onClick={() => onBucketClick?.(bucket.id)}
            >
              {/* Header */}
              <div className={`p-4 rounded-t-xl bg-gradient-to-r ${bucket.color}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon className="w-5 h-5 text-white" />
                    <span className="font-semibold text-white">{bucket.label}</span>
                  </div>
                  <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs text-white">
                    {bucket.items.length}
                  </span>
                </div>
              </div>

              {/* Progress bar */}
              {showProgress && (
                <div className="h-1 bg-gray-100">
                  <div
                    className={`h-full transition-all ${
                      status === 'complete' ? 'bg-green-500' : 'bg-blue-500'
                    }`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
              )}

              {/* Content preview */}
              <div className="p-4">
                {bucket.items.slice(0, 3).map((item) => (
                  <div
                    key={item.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      onItemClick?.(bucket.id, item.id);
                    }}
                    className="flex items-center gap-2 py-1.5 text-sm text-gray-700 hover:text-gray-900"
                  >
                    {getStatusIcon(item.status)}
                    <span className="truncate">{item.title}</span>
                    {item.aiScore && item.aiScore >= 80 && (
                      <Sparkles className="w-3 h-3 text-purple-500" />
                    )}
                  </div>
                ))}

                {bucket.items.length > 3 && (
                  <div className="text-xs text-gray-500 mt-2">
                    +{bucket.items.length - 3} more items
                  </div>
                )}

                {bucket.items.length === 0 && (
                  <div className="text-sm text-gray-400 text-center py-4">
                    No content yet
                  </div>
                )}
              </div>

              {/* Add button */}
              {onAddItem && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddItem(bucket.id);
                  }}
                  className="absolute bottom-4 right-4 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
                >
                  <Plus className="w-4 h-4 text-gray-600" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Flow layout (horizontal)
  if (layout === 'flow') {
    return (
      <div className={`flex items-start gap-4 overflow-x-auto pb-4 ${className}`}>
        {buckets.map((bucket, index) => {
          const progress = getBucketProgress(bucket);
          const Icon = bucket.icon;

          return (
            <React.Fragment key={bucket.id}>
              <div
                onClick={() => onBucketClick?.(bucket.id)}
                className="flex-shrink-0 w-48 bg-white rounded-xl border border-gray-200 hover:shadow-lg transition-all cursor-pointer"
              >
                <div className={`p-3 rounded-t-xl bg-gradient-to-r ${bucket.color}`}>
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-white" />
                    <span className="font-medium text-white text-sm">{bucket.label}</span>
                  </div>
                </div>
                <div className="p-3">
                  <div className="text-2xl font-bold text-gray-800">{bucket.items.length}</div>
                  <div className="text-xs text-gray-500">items</div>
                  <div className="mt-2 h-1 bg-gray-100 rounded-full">
                    <div
                      className="h-full bg-green-500 rounded-full"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              </div>

              {index < buckets.length - 1 && (
                <div className="flex-shrink-0 flex items-center self-center">
                  <ChevronRight className="w-5 h-5 text-gray-300" />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  // Compact layout
  return (
    <div className={`space-y-2 ${className}`}>
      {buckets.map((bucket) => {
        const progress = getBucketProgress(bucket);
        const Icon = bucket.icon;

        return (
          <button
            key={bucket.id}
            onClick={() => onBucketClick?.(bucket.id)}
            className="w-full flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            <div className={`w-8 h-8 rounded-lg bg-gradient-to-r ${bucket.color} flex items-center justify-center`}>
              <Icon className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 text-left">
              <div className="font-medium text-gray-800">{bucket.label}</div>
              <div className="text-xs text-gray-500">{bucket.items.length} items</div>
            </div>
            <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            <ChevronRight className="w-4 h-4 text-gray-400" />
          </button>
        );
      })}
    </div>
  );
};

export default ContentFlow;
